// Wires up the shared IAM permission-scanner (accessManager.bundle.js, loaded via
// public/index.html) — the same library used by social-app-fe. It scans the DOM
// for elements marked with `data-permission-access="<feature_code>"` and, based on
// the IAM permission-matrix response for the current user/org, sets
// `pointer-events: none` / `data-access-restricted="true"` on restricted ones.
//
// This does NOT work on native <option> elements (browsers largely ignore
// pointer-events/attribute changes on them) — features gated inside a <select>
// must check the permission matrix directly in React instead. See
// src/lib/use-permission.ts.
import { isAtsEmbedActive } from "../hoc/api/ats-embed";
export function launchPermissionWatch() {
    if (typeof window === "undefined")
        return;
    // Skip inside the ATS embed. There the signed-in recruiter is a cross-app user
    // (provisioned in the Vriti app), so the permission-matrix scan — keyed to THIS
    // standalone app's identity — doesn't apply, and its DOM mutations blanked the
    // embedded view. Feature gating in the embed is enforced by the backend anyway.
    if (isAtsEmbedActive())
        return;
    // Skip on local dev (localhost/127.0.0.1). The prebuilt observer's self-
    // triggering MutationObserver saturates the main thread on the dev server and
    // blanks the page ("home for a few seconds then blank") — a local-only artifact.
    // SIT / dev / prod builds run it normally (it works there).
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1")
        return;
    // Idempotency guard. `permissionWatch` installs a MutationObserver that also
    // injects <style> tags; calling it more than once stacks observers that then
    // react to each other's mutations — a runaway "MUTATION LOOP" that saturates
    // the main thread and blanks the page. The flag lives on `window` (not module
    // scope) so it also survives webpack HMR, which re-runs the entry module — and
    // therefore this call — on every hot reload during development.
    if (window.__permissionWatchStarted)
        return;
    window.__permissionWatchStarted = true;
    const start = () => {
        const AccessManager = window.AccessManager;
        if (!(AccessManager === null || AccessManager === void 0 ? void 0 : AccessManager.permissionWatch)) {
            // Bundle script loads with `defer`; retry briefly in case this runs first.
            setTimeout(start, 100);
            return;
        }
        const config = {
            appIdentifier: process.env.NEXT_PUBLIC_IAM_APP_IDENTIFIER,
            customerSecretKey: process.env.NEXT_PUBLIC_IAM_SECRET_KEY,
            permissionApiUrl: `${process.env.NEXT_PUBLIC_IAM_ROUTE}/permission-matrix`,
            tokenKey: "localstorage.access",
            permissionMap: {
                name: "feature_code",
                allow: "allow_flag",
                readonly: "read_flag",
                masked: "mask_flag",
                restricted: "restrict_flag",
                hidden: "hidden_flag",
                restrictedMessage: "restrict_message",
                maskedMessage: "masked_message",
                apiType: "api_type",
            },
            maskedPlaceholder: "********",
        };
        AccessManager.permissionWatch(config);
    };
    if (document.readyState === "complete" || document.readyState === "interactive") {
        start();
    }
    else {
        document.addEventListener("DOMContentLoaded", start);
    }
}
export default launchPermissionWatch;
